/**
 * Copyright (C) Siemens AG 1996-2025 ALL RIGHTS RESERVED.
 */

import {
  Component,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {AbstractControl, ReactiveFormsModule} from '@angular/forms';
import {BaseFormFieldDirective} from '../../base-form-field.directive';
import {FieldLabelComponent} from '../field-label/field-label.component';
import {IxModule, ModalService} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {DocumentInputComponent} from '../document-input/document-input.component';
import {EmailInputComponent} from '../email-input/email-input.component';
import {TextInputComponent} from '../text-input/text-input.component';
import {EnumInputComponent} from '../enum-input/enum-input.component';
import {NumberInputComponent} from '../number-input/number-input.component';
import {BooleanInputComponent} from '../boolean-input/boolean-input.component';
import {takeUntil} from 'rxjs';

import {NormalisedApiError} from '../../../../../../../models/errors/api-error';
import {AppSettings} from '../../../../../../../models/Constants';

@Component({
  selector: 'app-address-input',
  imports: [
    CommonModule,
    FieldLabelComponent,
    IxModule,
    TranslateModule,
    ReactiveFormsModule,
    DocumentInputComponent,
    EmailInputComponent,
    TextInputComponent,
    EnumInputComponent,
    NumberInputComponent,
    BooleanInputComponent,
  ],
  templateUrl: './address-input.component.html',
  styleUrl: '../input-components.scss',
})
export class AddressInputComponent
  extends BaseFormFieldDirective
  implements OnChanges
{
  private readonly modalService = inject(ModalService);

  @ViewChild('addressModal', {read: TemplateRef})
  addressModalRef!: TemplateRef<never>;

  errorInAddress = false;
  addressValueCombined = '';

  constructor() {
    super();
    this.passportService.onUnprocessableEntityError
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        if (!error) {
          this.errorInAddress = false;
        }
      });
  }

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (changes['passData']) {
      this.passData = changes['passData'].currentValue;
      this.setCombinedAddressValue();
    }
  }

  protected override clearErrors(): void {
    super.clearErrors();
    this.errorInAddress = false;
  }

  override processUnprocessableEntityErrors(
    normalisedApiError: NormalisedApiError,
    _: AbstractControl,
    fullValuePath: string,
  ) {
    // Reset normalisedApiError state
    this.errorInAddress = false;

    // Collect all attribute paths from the address sub-attributes
    const addressAttributePaths: string[] = [];
    this.attribute.attributes?.forEach((attr) => {
      // Build the full path: attribute.valuePath + '.' + attr.valuePath
      const fullPath = `${this.attribute.valuePath}.${attr.valuePath}`;
      addressAttributePaths.push(fullPath);
    });
    // Also add the address attribute itself
    addressAttributePaths.push(this.attribute.valuePath);

    for (const entry of normalisedApiError.all) {
      const code = entry.code ?? '';
      const jsonPathField = this.normalizeFieldPath(
        entry.source?.pointer ?? '',
      );
      const isMissingRequired = code === 'MISSING_REQUIRED_PROPERTY';
      // Pointer matches either the address field itself (e.g. the whole
      // `postalAddress` object is missing) or one of its sub-attributes exactly.
      const pointsToExactField =
        jsonPathField === fullValuePath ||
        addressAttributePaths.includes(jsonPathField);
      const bypassRequiredCheck = pointsToExactField && isMissingRequired;

      if (
        (pointsToExactField || fullValuePath.startsWith(jsonPathField)) &&
        (bypassRequiredCheck || this.isAttributeRequired())
      ) {
        this.errorInAddress = true;
        break; // Exit loop early if we found an normalisedApiError
      }
    }
  }

  async openAddressModal() {
    const instance = await this.modalService.open({
      content: this.addressModalRef,
      data: this.attribute,
    });

    instance.onClose.on(() => {
      setTimeout(() => {
        this.setCombinedAddressValue();
      }, AppSettings.INPUT_TO_REQUEST_WAIT_TIME_IN_MS);
    });
  }

  saveAddress(modal: {close: () => void}) {
    this.errorInAddress = false;
    modal.close();
  }

  setCombinedAddressValue() {
    const addressAttributeArray: string[] = [];
    this.attribute.attributes?.forEach((attr) => {
      const value = this.generalDataHandlingService.getValueByPath(
        this.passData,
        attr.valuePath,
      );
      if (value) {
        addressAttributeArray.push(value);
      }
    });
    if (addressAttributeArray.length) {
      this.addressValueCombined = addressAttributeArray.join(', ');
    } else {
      this.addressValueCombined = '';
    }
  }
}
